import lottie, { type AnimationItem } from 'lottie-web';

export interface BakeOptions {
  width?: number;
  height?: number;
  fps?: number;
  maxFrames?: number;
  onProgress?: (current: number, total: number) => void;
}

export interface BakeResult {
  blob: Blob;
  width: number;
  height: number;
  fps: number;
  frameCount: number;
  durationMs: number;
}

const DEFAULT_MAX_FRAMES = 1800;

export function pickWebmMimeType(): string {
  const candidates = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp09.00.10.08',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const mime of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }
  throw new Error('No supported WebM MediaRecorder mime type in this browser');
}

function readLottieDimensions(lottieJson: { w?: number; h?: number; fr?: number; ip?: number; op?: number; }): { width: number; height: number; fps: number; frameCount: number; } {
  const width = Number(lottieJson.w);
  const height = Number(lottieJson.h);
  const fps = Number(lottieJson.fr);
  const ip = Number(lottieJson.ip ?? 0);
  const op = Number(lottieJson.op ?? 0);
  if (!width || !height) throw new Error('Lottie JSON missing width/height (w/h)');
  if (!fps) throw new Error('Lottie JSON missing framerate (fr)');
  if (op <= ip) throw new Error('Lottie JSON has no frames (op <= ip)');
  return { width, height, fps, frameCount: Math.round(op - ip) };
}

export async function bakeLottieToWebM(
  lottieJson: Record<string, unknown>,
  opts: BakeOptions = {}
): Promise<BakeResult> {
  const meta = readLottieDimensions(lottieJson as { w?: number; h?: number; fr?: number; ip?: number; op?: number; });
  const width = opts.width ?? meta.width;
  const height = opts.height ?? meta.height;
  const fps = opts.fps ?? meta.fps;
  const frameCount = Math.min(meta.frameCount, opts.maxFrames ?? DEFAULT_MAX_FRAMES);
  const mimeType = pickWebmMimeType();

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  container.appendChild(canvas);
  document.body.appendChild(container);

  let animation: AnimationItem | null = null;
  try {
    animation = lottie.loadAnimation({
      container,
      renderer: 'canvas',
      loop: false,
      autoplay: false,
      animationData: lottieJson as object,
      rendererSettings: {
        context: canvas.getContext('2d', { alpha: true }) ?? undefined,
        clearCanvas: true,
        preserveAspectRatio: 'xMidYMid meet',
      },
    });

    const stream = canvas.captureStream(0);
    const videoTrack = stream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack | undefined;
    if (!videoTrack || typeof videoTrack.requestFrame !== 'function') {
      throw new Error('CanvasCaptureMediaStreamTrack.requestFrame is unavailable in this browser');
    }

    const chunks: Blob[] = [];
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 8_000_000 });
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    const stopped = new Promise<void>((resolve) => { recorder.onstop = () => resolve(); });

    recorder.start();

    // Render + push frames deterministically. Pace via rAF so the browser has time
    // to flush each frame through the MediaStreamTrack before we request the next.
    const frameIntervalMs = 1000 / fps;
    for (let i = 0; i < frameCount; i++) {
      animation.goToAndStop(i, true);
      videoTrack.requestFrame();
      opts.onProgress?.(i + 1, frameCount);
      await new Promise<void>((resolve) => setTimeout(resolve, frameIntervalMs));
    }

    recorder.stop();
    await stopped;

    const blob = new Blob(chunks, { type: mimeType });
    return {
      blob,
      width,
      height,
      fps,
      frameCount,
      durationMs: Math.round((frameCount / fps) * 1000),
    };
  } finally {
    animation?.destroy();
    container.remove();
  }
}

export async function fileIsLottieJson(file: File): Promise<boolean> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.lottie')) return true;
  if (!name.endsWith('.json')) return false;
  try {
    const text = await file.text();
    const parsed = JSON.parse(text) as Record<string, unknown>;
    return typeof parsed.v === 'string' && typeof parsed.w === 'number' && typeof parsed.h === 'number' && Array.isArray(parsed.layers);
  } catch {
    return false;
  }
}

export async function readLottieJsonFromFile(file: File): Promise<Record<string, unknown>> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.lottie')) {
    throw new Error('.lottie (zipped) files are not yet supported — please upload the unzipped .json');
  }
  const text = await file.text();
  return JSON.parse(text) as Record<string, unknown>;
}
